import type { ReactNode } from 'react';

type CalloutTone = 'danger' | 'success' | 'warning' | 'info';

const TONE_BOX: Record<CalloutTone, string> = {
  danger: 'border-danger/30 bg-danger-soft',
  success: 'border-success/30 bg-success-soft',
  warning: 'border-warning/30 bg-warning-soft',
  info: 'border-info/30 bg-info-soft',
};

const TONE_KICKER: Record<CalloutTone, string> = {
  danger: 'text-danger',
  success: 'text-success',
  warning: 'text-warning',
  info: 'text-info',
};

/**
 * The one banner. Replaces every hand-rolled error/success/amber notice.
 * Feed mutation errors through errorMessage() from lib/api at the call site.
 */
export function Callout({
  tone,
  kicker,
  actions,
  detail,
  className = '',
  children,
}: {
  tone: CalloutTone;
  kicker?: ReactNode;
  actions?: ReactNode;
  detail?: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : undefined}
      className={`flex flex-col gap-2 rounded-lg border px-4 py-3 text-sm ${TONE_BOX[tone]} ${className}`}
    >
      {kicker && (
        <div className={`text-2xs font-semibold uppercase tracking-wide ${TONE_KICKER[tone]}`}>
          {kicker}
        </div>
      )}
      {children != null && <div className="text-ink-muted">{children}</div>}
      {detail && (
        <pre className="overflow-x-auto rounded-[5px] bg-surface-2 px-3 py-2 font-mono text-xs text-ink-muted border border-line">
          {detail}
        </pre>
      )}
      {actions && <div className="flex flex-wrap items-center gap-2 pt-1">{actions}</div>}
    </div>
  );
}
